import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AllIcon, ShipmentsIcon, WeatherIcon, GrowingIcon, CombineIcon } from '../../../components/icons';
import MapCombinePopover from './MapCombinePopover';
import MapViewIcon from './MapViewIcon';
import { LAYER_MODES } from '../../../utils/mapUrlParams';
import { allCombinationsSaved } from '../../../utils/mapViews';
import { floatingSurfaceClass } from '../floatingSurfaceStyles';

const TABS = [
  { id: LAYER_MODES.ALL, fallback: 'All', Icon: AllIcon },
  { id: LAYER_MODES.SHIPMENTS, fallback: 'Shipments', Icon: ShipmentsIcon },
  { id: LAYER_MODES.WEATHER, fallback: 'Weather', Icon: WeatherIcon },
  { id: LAYER_MODES.GROWING, fallback: 'Growing', Icon: GrowingIcon },
];

// Colours are literal class strings — Tailwind only emits what it can read in
// the source, so none of this can be built from runtime values.
const SURFACE = floatingSurfaceClass(94);
const SEPARATOR = 'bg-[#EEF0EC] dark:bg-[#2A302C]';
// Collapsed phone strip: white at 45% behind a 12px blur, falling back to 85%
// where backdrop-filter is unsupported (`supports-` emits the blur rule only
// when the browser accepts it, so the 85% base shows through otherwise).
const STRIP_SURFACE = 'block bg-white/85 supports-[backdrop-filter]:bg-white/45 supports-[backdrop-filter]:backdrop-blur-[12px] dark:bg-[#1B1F1C]/85 dark:supports-[backdrop-filter]:bg-[#1B1F1C]/45';
const FOCUS = 'focus-visible:outline-[#16A34A] dark:focus-visible:outline-[#7EE6A4]';

const COLOR_IDLE = 'text-[#5D625F] dark:text-[#9AA09A] group-hover:bg-[#F0FDF4] group-hover:text-[#16A34A] dark:group-hover:bg-[#1C3023] dark:group-hover:text-[#7EE6A4] group-active:bg-[#DCF0E2] group-active:text-[#16A34A] dark:group-active:bg-[#1C3D28] dark:group-active:text-[#7EE6A4]';
const COLOR_OPEN = 'bg-[#F0FDF4] text-[#16A34A] dark:bg-[#1C3023] dark:text-[#7EE6A4]';
const COLOR_SELECTED = 'bg-[#16A34A] text-white shadow-[inset_0px_0px_0px_1px_#B7E4C7] dark:bg-[#22C55E] dark:text-[#191919] dark:shadow-[inset_0px_0px_0px_1px_#FFFFFF2E]';
// Combine's glyph is green at rest while its label stays grey, so it opts out
// of the tile's text colour until the tab is hovered or pressed.
const COLOR_COMBINE_ICON = 'text-[#16A34A] dark:text-[#22C55E] dark:group-hover:text-[#7EE6A4] dark:group-active:text-[#7EE6A4]';

// 56x36 at rest, 60x60 while hovered or pressed, 56x56 once selected.
//
// The growth is visual only: the button keeps its resting footprint and the
// tile inside it is absolutely centred, so an expanding tab spills into the
// 16px gap instead of reflowing the rail. Without that, hovering one tab
// resized the whole panel and shoved every other tab out from under the cursor.
//
// A 36px slot grows 12px past each edge, which is what sets the rail's padding:
// 16px top/bottom against 8px left/right. That leaves the first and last tabs
// the same 4px of clearance when expanded that the 16px gaps leave between
// neighbours, so a hovered tile never runs into the panel's border.
// Phones get square 40px tiles in a 56px rail and skip the grow-on-hover
// treatment entirely — there is no hover on touch, and a 60px tile would
// overflow the narrower panel.
const SLOT_IDLE = 'h-[38px] w-[38px] lg:h-[36px] lg:w-[56px]';
const SLOT_SELECTED = 'h-[38px] w-[38px] lg:h-[56px] lg:w-[56px]';
const TILE_IDLE = 'h-[38px] w-[38px] lg:h-[36px] lg:w-[56px] lg:group-hover:h-[60px] lg:group-hover:w-[60px] lg:group-active:h-[60px] lg:group-active:w-[60px]';
const TILE_SELECTED = 'h-[38px] w-[38px] lg:h-[56px] lg:w-[56px]';
// Combine holds the full hover treatment — size included — while its popover is
// open, so the tab doesn't shrink back to a pill under the panel it opened.
const TILE_OPEN = 'h-[60px] w-[60px]';
// 20px glyph at 1.5 stroke, 32px at 1.9. The stroke does not scale with the
// glyph, so the expanded states restate it in viewBox units (1.9 / 32 * 20)
// rather than letting the 1.5 attribute stretch to 2.4.
const ICON_STROKE_EXPANDED = '[stroke-width:1.1875]';
const ICON_OPEN = `h-[32px] w-[32px] ${ICON_STROKE_EXPANDED}`;
const ICON_IDLE = 'h-[20px] w-[20px] lg:group-hover:h-[32px] lg:group-hover:w-[32px] lg:group-hover:[stroke-width:1.1875] lg:group-active:h-[32px] lg:group-active:w-[32px] lg:group-active:[stroke-width:1.1875]';
const ICON_SELECTED = 'h-[20px] w-[20px]';

const RailButton = ({
  label, Icon, selected, iconClassName = '', forceActive = false,
  // Saved views only: their generated names are too long for the tabs' single
  // nowrap line, and the design shows them on two.
  wrapLabel = false,
  glyph = null,
  className = '',
  ...rest
}) => (
  <button
    type="button"
    className={`group relative shrink-0 rounded-[10px] hover:z-10 focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${FOCUS} ${
      selected ? SLOT_SELECTED : SLOT_IDLE
    } ${className}`}
    {...rest}
  >
    <span
      className={`absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-[4px] rounded-[10px] transition-all duration-200 ease-out ${
        selected
          ? `${TILE_SELECTED} ${COLOR_SELECTED}`
          : forceActive
            ? `${TILE_OPEN} ${COLOR_OPEN}`
            : `${TILE_IDLE} ${COLOR_IDLE}`
      }`}
    >
      {glyph || (
        <Icon
          className={`shrink-0 transition-all duration-200 ease-out ${
            selected ? ICON_SELECTED : forceActive ? ICON_OPEN : ICON_IDLE
          } ${iconClassName}`}
        />
      )}
      <span
        className={`hidden px-[2px] text-center text-[10px] leading-[12px] lg:block ${
          wrapLabel ? 'break-words' : 'whitespace-nowrap'
        } ${selected ? 'font-semibold' : 'font-bold'}`}
      >
        {label}
      </span>
    </span>
  </button>
);

/**
 * Vertical layer rail pinned to the right edge of the map — rounded on its left
 * side only so it reads as attached to the map frame.
 *
 * The tabs are fully controlled — the rail renders `value` and reports clicks,
 * it holds no layer state. The only state here is whether Combine's popover is
 * open, which is nobody else's business.
 */
const COLLAPSE_KEY = 'bloomiq.map.railCollapsed';

/** Reads the remembered phone state; collapsed is the first-load default. */
const readCollapsed = () => {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) !== 'false';
  } catch {
    return true;
  }
};

const MapLayerRail = ({
  value = LAYER_MODES.ALL,
  onSelect,
  onCombine,
  // Saved views ("Combine layers"). `activeViewId` is separate from `value`
  // because a view is not a layer mode — while one is active the layer tabs
  // show nothing selected, since the map is showing a set rather than one.
  views = [],
  activeViewId = null,
  onSelectView,
  onUpdateView,
  onDeleteView,
  className = '',
}) => {
  const { t } = useTranslation();
  const [combineOpen, setCombineOpen] = useState(false);
  const combineAnchor = useRef(null);
  // Which saved view is being edited, if any. Only one popover is ever open:
  // opening either closes the other, so they cannot overlap on the rail.
  const [editingViewId, setEditingViewId] = useState(null);
  const editAnchor = useRef(null);
  const popoverOpen = combineOpen || editingViewId != null;
  // With three layers there are only three allowed pairs. Once all are saved
  // Combine has nothing to offer — its popover would open with every row
  // ticked into an existing view and a Save that can never enable — so the
  // button is disabled rather than leading to a dead end.
  const combineExhausted = allCombinationsSaved(views);
  // Phone only — desktop ignores this and always renders the full rail.
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const touchStartX = useRef(null);

  const setCollapsedPersisted = (next) => {
    setCollapsed(next);
    setCombineOpen(false);
    try {
      window.localStorage.setItem(COLLAPSE_KEY, String(next));
    } catch {
      /* private mode — the state just won't survive a reload */
    }
  };

  // Long-press a saved view to edit it. Touch has no hover, so the pencil
  // shows only on the selected view; holding any tile reaches the same
  // popover without having to activate that view first.
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  useEffect(() => cancelLongPress, []);

  const viewHoldHandlers = (id) => ({
    onTouchStart: () => {
      longPressFired.current = false;
      cancelLongPress();
      longPressTimer.current = setTimeout(() => {
        longPressFired.current = true;
        setCombineOpen(false);
        setEditingViewId(id);
      }, 500);
    },
    // A drag is the rail's collapse swipe, not a hold.
    onTouchMove: cancelLongPress,
    onTouchEnd: cancelLongPress,
    onTouchCancel: cancelLongPress,
  });

  // Swipe left on the strip expands, swipe right on the rail collapses.
  const onTouchStart = (e) => { touchStartX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (next) => (e) => {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? start) - start;
    if (next ? dx > 24 : dx < -24) setCollapsedPersisted(next);
  };

  const activeIndex = Math.max(0, TABS.findIndex((tab) => tab.id === value));

  return (
    <>
      {/* Collapsed phone strip: a 12px translucent edge with a tick at the
          active mode and a ‹ tab in the middle. The button around it is
          44px wide — the strip itself is far too thin to hit.
          The chevron carries the rail's own opaque surface rather than sitting
          bare on the tiles: at 14px with no backing it disappeared over dark
          ocean and over any marker it happened to land on, so the one control
          that reopens the rail was effectively invisible. */}
      <div
        className={`absolute right-0 top-1/2 z-[1000] -translate-y-1/2 transition-opacity duration-[220ms] ease-out lg:hidden ${
          collapsed ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        <button
          type="button"
          onClick={() => setCollapsedPersisted(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd(false)}
          aria-label={t('map.layers.expandRail', 'Show map layers')}
          aria-expanded={!collapsed}
          className="relative flex h-[250px] w-[44px] cursor-pointer items-center justify-end"
        >
          <span className={`relative h-[250px] w-[12px] rounded-l-[6px] ${STRIP_SURFACE}`}>
            {!activeViewId && (
              <span
                className="absolute right-0 h-[28px] w-[4px] rounded-l-[2px] bg-[#16A34A]"
                style={{ top: `${((activeIndex + 0.5) / TABS.length) * 100}%`, transform: 'translateY(-50%)' }}
              />
            )}
          </span>
          <span
            className={`absolute right-0 top-1/2 flex h-[52px] w-[26px] -translate-y-1/2 items-center justify-center rounded-l-[10px] border border-solid border-r-0 ${SURFACE}`}
          >
            <ChevronLeft size={18} className="text-[#5D625F] dark:text-[#9AA09A]" />
          </span>
        </button>
      </div>

      <div
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd(true)}
        // The rail sits below MapPage's overlay column (z-1100) so the filter
        // stack reads over it. A popover opens INSIDE the rail, though, and the
        // rail's own z-index makes a stacking context — so no z-index on the
        // popover can lift it past that column, and the Arrivals and Season
        // summary pills painted straight over it. The rail outranks the column
        // only while a popover is open, which is the one moment it has to.
        className={`absolute right-0 top-1/2 ${popoverOpen ? 'z-[1200]' : 'z-[1000]'} flex w-[48px] -translate-y-1/2 flex-col items-center gap-[10px] border border-solid px-[5px] py-[10px] rounded-l-[12px] transition-transform duration-[220ms] ease-out lg:w-[72px] lg:gap-[16px] lg:px-[7px] lg:py-[15px] lg:translate-x-0 ${
          collapsed ? 'max-lg:translate-x-full' : ''
        } ${SURFACE} ${className}`}
      >
      <div
        role="radiogroup"
        aria-label={t('map.layers.railAria', 'Map layers')}
        className="flex w-full flex-col items-center gap-[12px] lg:gap-[16px]"
      >
        {TABS.map(({ id, fallback, Icon }) => {
          // A live saved view owns the selection: the map is showing a set,
          // not one layer, so leaving a tab lit would claim otherwise.
          const on = !activeViewId && value === id;
          return (
            <RailButton
              key={id}
              role="radio"
              aria-checked={on}
              onClick={() => onSelect?.(id)}
              label={t(`map.layers.${id}`, fallback)}
              Icon={Icon}
              selected={on}
            />
          );
        })}
      </div>

      <div className={`h-px w-full shrink-0 ${SEPARATOR}`} />

      {/* My views. The label and the whole block appear with the first saved
          view and vanish with the last, so an empty rail never shows a
          heading with nothing under it. */}
      {views.length > 0 && (
        <>
          <div className="flex w-full flex-col items-center gap-[10px] lg:gap-[12px]">
            <span className="hidden w-full px-[2px] text-center font-['Inter'] text-[9px] font-extrabold uppercase tracking-[0.63px] text-[#9CA3AF] lg:block dark:text-[#6B716C]">
              {t('map.views.myViews', 'My views')}
            </span>
            {views.map((v) => (
              <div
                key={v.id}
                ref={editingViewId === v.id ? editAnchor : null}
                className="group/view relative w-full"
                {...viewHoldHandlers(v.id)}
              >
                <RailButton
                  aria-pressed={activeViewId === v.id}
                  // touchend still fires a click, so a hold that opened the
                  // editor must not also switch the map to that view.
                  onClick={() => {
                    if (longPressFired.current) {
                      longPressFired.current = false;
                      return;
                    }
                    onSelectView?.(v.id);
                  }}
                  label={v.name}
                  wrapLabel
                  glyph={<MapViewIcon layers={v.layers} />}
                  selected={activeViewId === v.id}
                />

                {/* Edit badge. Shown on hover, and kept visible while its own
                    popover is open — otherwise moving the cursor off the tile
                    to reach the popover would hide the control that opened it.
                    Its own button, not nested in the tile's, so activating a
                    view and editing it stay separate clicks.

                    Pointer devices only. A touch device has no hover, so the
                    badge would have to sit permanently on the tile — a row of
                    pencils in a 48px rail — and its tap target would crowd
                    the corner of the tile it sits on. Hold the tile instead
                    (see `viewHoldHandlers`), which opens the same popover.
                    `(hover: none)` asks the device whether it can hover at
                    all, which is the actual condition; a width breakpoint
                    would get a touchscreen laptop wrong in one direction and
                    a mouse-driven small window in the other.

                    `before:` widens the tap target: it takes the 18px disc to
                    ~32px without changing how it looks. */}
                <button
                  type="button"
                  aria-label={t('map.views.edit', 'Edit view')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCombineOpen(false);
                    setEditingViewId((id) => (id === v.id ? null : v.id));
                  }}
                  className={`absolute -right-[2px] -top-[2px] z-10 flex h-[18px] w-[18px] items-center justify-center rounded-full border border-solid border-[#E6E9E4] bg-white text-[#5D625F] shadow-[0px_1px_2px_rgba(17,24,39,0.12)] transition-opacity before:absolute before:-inset-[7px] before:content-[''] [@media(hover:none)]:hidden dark:border-[#2F3531] dark:bg-[#1F2420] dark:text-[#9AA09A] ${
                    editingViewId === v.id
                      ? 'opacity-100'
                      : 'opacity-0 group-hover/view:opacity-100 focus-visible:opacity-100'
                  }`}
                >
                  <Pencil size={10} />
                </button>

                {editingViewId === v.id && (
                  <MapCombinePopover
                    className="absolute right-full top-0 mr-[16px]"
                    value={v.layers}
                    anchorRef={editAnchor}
                    onClose={() => setEditingViewId(null)}
                    onSave={(next) => {
                      onUpdateView?.(v.id, next);
                      setEditingViewId(null);
                    }}
                    onDelete={() => {
                      onDeleteView?.(v.id);
                      setEditingViewId(null);
                    }}
                  />
                )}
              </div>
            ))}
          </div>

          <div className={`h-px w-full shrink-0 ${SEPARATOR}`} />
        </>
      )}

      <div ref={combineAnchor} className="relative shrink-0">
        <RailButton
          aria-haspopup="dialog"
          aria-expanded={combineOpen}
          disabled={combineExhausted}
          title={combineExhausted
            ? t('map.combine.allSaved', 'Every combination is already saved')
            : undefined}
          onClick={() => {
            if (combineExhausted) return;
            setEditingViewId(null);
            setCombineOpen((o) => !o);
          }}
          label={t('map.layers.combine', 'Combine')}
          Icon={CombineIcon}
          selected={false}
          forceActive={combineOpen}
          iconClassName={combineOpen ? '' : COLOR_COMBINE_ICON}
          className={combineExhausted ? 'cursor-default opacity-40' : ''}
        />
        {combineOpen && !combineExhausted && (
          <MapCombinePopover
            className="absolute bottom-0 right-full mr-[16px]"
            anchorRef={combineAnchor}
            onClose={() => setCombineOpen(false)}
            onSave={(next) => {
              onCombine?.(next);
              setCombineOpen(false);
            }}
          />
        )}
      </div>

      {/* › collapses the rail; phone only. */}
      <button
        type="button"
        onClick={() => setCollapsedPersisted(true)}
        aria-label={t('map.layers.collapseRail', 'Hide map layers')}
        className="flex h-[32px] w-full shrink-0 cursor-pointer items-center justify-center rounded-[8px] text-[#5D625F] dark:text-[#9AA09A] lg:hidden"
      >
        <ChevronRight size={18} />
      </button>
      </div>
    </>
  );
};

export default MapLayerRail;
